/**
 * TanStack Query hooks for changing a plan's dates (RP-28) — src/api/plan-dates.ts.
 *
 * The preview is a query, not a mutation: it writes nothing and is a pure function of the
 * request, so caching it per request lets the planner flip between Move and Fit without
 * refetching. Apply invalidates every plan key, as every plan mutation does.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { send, useInvalidatePlans } from "@/hooks/use-resource-plans";
import { formatPlanMonth } from "@/lib/plan-format";
import { resourcePlanKeys } from "@/lib/query";
import type { PlanDatesPreviewView, PlanDatesStaffingReviewView } from "@/schemas/plan-dates";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

export interface PlanDatesRequest {
  mode: "Move" | "Fit";
  startMonth: string;
  /** Fit only; null clears the end on a Monthly plan. */
  endMonth?: string | null;
  reason?: string;
}

/** What the change would do. `request` null = nothing valid to ask yet. */
export function usePlanDatesPreview(
  planId: string,
  planUpdatedAt: string,
  request: PlanDatesRequest | null,
) {
  return useQuery({
    queryKey: resourcePlanKeys.datesPreview(
      planId,
      planUpdatedAt,
      request ?? { mode: "", startMonth: "", endMonth: null },
    ),
    queryFn: () => {
      if (request === null) throw new Error("No dates to preview");
      const { reason: _reason, ...body } = request;
      return send<PlanDatesPreviewView>(`/api/plans/${planId}/dates`, "POST", body);
    },
    enabled: request !== null,
    retry: false,
    gcTime: 0,
  });
}

/** Make the change. The server recomputes from the same request. */
export function useApplyPlanDates(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (request: PlanDatesRequest) =>
      send<ResourcePlanDetailView>(`/api/plans/${planId}/dates/apply`, "POST", request),
    onSuccess: (plan) => {
      invalidate();
      toast.success(
        `Dates changed — ${formatPlanMonth(plan.startMonth)} to ${
          plan.endMonth === null ? "the Planning Horizon" : formatPlanMonth(plan.endMonth)
        }`,
      );
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to change the dates"),
  });
}

/** RP-28.3 — has a Staffed plan's dates changed since it was staffed? */
export function usePlanDatesStaffingReview(planId: string, enabled: boolean) {
  return useQuery({
    queryKey: resourcePlanKeys.datesStaffingReview(planId),
    queryFn: () =>
      send<PlanDatesStaffingReviewView>(`/api/plans/${planId}/dates/staffing-review`, "GET"),
    enabled,
  });
}
